// ============================================================
// ALL IS WELL — HEALTH MEMBERSHIPS DATA
// IMPORTANT: Do NOT invent actual membership benefits, prices,
// or discounts. Use verified All Is Well membership data only.
// This is prototype/placeholder data — clearly marked.
// ============================================================

export const MEMBERSHIPS_DATA_STATUS = 'PROTOTYPE' as const;
// When real membership data is available from All Is Well,
// replace this data with verified information.

export interface MembershipCategory {
  id: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  forWhom: string;
}

export interface MembershipPlan {
  id: string;
  categoryId: string;
  name: string;
  forWhom: string;
  // Benefits, pricing, validity — to be filled with verified data
  benefits: string[]; // Only verified benefits
  validity?: string;  // Only if verified
  participatingCentreIds: string[]; // Only verified centres
  contactForDetails: boolean; // If true, show "Contact centre for details"
  isVerified: boolean; // false = prototype placeholder
}

// ============================================================
// MEMBERSHIP CATEGORIES
// ============================================================
export const membershipCategories: MembershipCategory[] = [
  {
    id: 'family-health',
    label: 'FAMILY HEALTH',
    description: 'Comprehensive care for the whole family',
    icon: 'Users',
    color: 'blue',
    forWhom: 'Families',
  },
  {
    id: 'senior-care',
    label: 'SENIOR CARE',
    description: 'Dedicated care for senior members',
    icon: 'HeartHandshake',
    color: 'amber',
    forWhom: 'Adults 60+',
  },
  {
    id: 'womens-health',
    label: "WOMEN'S HEALTH",
    description: 'Focused care for women at every stage',
    icon: 'Heart',
    color: 'pink',
    forWhom: 'Women',
  },
  {
    id: 'child-health',
    label: 'CHILD HEALTH',
    description: 'Preventive and curative care for children',
    icon: 'Baby',
    color: 'emerald',
    forWhom: 'Children 0–18',
  },
  {
    id: 'preventive-health',
    label: 'PREVENTIVE HEALTH',
    description: 'Stay ahead with regular health monitoring',
    icon: 'ShieldCheck',
    color: 'teal',
    forWhom: 'Adults',
  },
  {
    id: 'chronic-care',
    label: 'CHRONIC CARE',
    description: 'Continuous support for long-term conditions',
    icon: 'Activity',
    color: 'violet',
    forWhom: 'Chronic condition patients',
  },
];

// ============================================================
// MEMBERSHIP PLANS — Prototype placeholders
// All plans marked isVerified: false until confirmed by All Is Well
// ============================================================
export const membershipPlans: MembershipPlan[] = [
  {
    id: 'family-health-plan',
    categoryId: 'family-health',
    name: 'All Is Well Family Health Plan',
    forWhom: 'Families (up to 4 members)',
    benefits: [], // To be filled with verified benefits
    participatingCentreIds: ['burhanpur-super-clinic'],
    contactForDetails: true,
    isVerified: false,
  },
  {
    id: 'senior-care-plan',
    categoryId: 'senior-care',
    name: 'All Is Well Senior Care Plan',
    forWhom: 'Adults aged 60 and above',
    benefits: [], // To be filled with verified benefits
    participatingCentreIds: ['burhanpur-super-clinic'],
    contactForDetails: true,
    isVerified: false,
  },
  {
    id: 'womens-health-plan',
    categoryId: 'womens-health',
    name: "All Is Well Women's Health Plan",
    forWhom: 'Women',
    benefits: [], // To be filled with verified benefits
    participatingCentreIds: ['burhanpur-super-clinic'],
    contactForDetails: true,
    isVerified: false,
  },
  {
    id: 'child-health-plan',
    categoryId: 'child-health',
    name: 'All Is Well Child Health Plan',
    forWhom: 'Children aged 0–18',
    benefits: [], // To be filled with verified benefits
    participatingCentreIds: ['burhanpur-super-clinic'],
    contactForDetails: true,
    isVerified: false,
  },
  {
    id: 'preventive-health-plan',
    categoryId: 'preventive-health',
    name: 'All Is Well Preventive Health Plan',
    forWhom: 'Adults seeking regular health monitoring',
    benefits: [], // To be filled with verified benefits
    participatingCentreIds: ['burhanpur-super-clinic'],
    contactForDetails: true,
    isVerified: false,
  },
  {
    id: 'chronic-care-plan',
    categoryId: 'chronic-care',
    name: 'All Is Well Chronic Care Plan',
    forWhom: 'Patients managing long-term conditions',
    benefits: [], // To be filled with verified benefits
    participatingCentreIds: ['burhanpur-super-clinic'],
    contactForDetails: true,
    isVerified: false,
  },
];

export const getMembershipCategoryById = (id: string): MembershipCategory | undefined =>
  membershipCategories.find((c) => c.id === id);

export const getMembershipPlanByCategory = (categoryId: string): MembershipPlan | undefined =>
  membershipPlans.find((p) => p.categoryId === categoryId);
